/**
 * Coding-buddy state document schemas and Firebase paths.
 *
 * Single source of truth for everything the Coding Buddies feature
 * persists per (user, course): identity (palette + proportions +
 * name), progression (intro seen, per-problem entries), and
 * engagement metadata (streak, when added). Combined into one doc
 * so the entire feature loads in a single read and writes in a
 * single set; the state is small enough (a few hundred bytes) that
 * Firestore's 1 MB doc limit is never a concern.
 */

import {z} from 'zod';

import type {CourseId} from '../../../types/branded';
import {FirestoreTimestampSchema} from '../../types';

// ============================================================================
// Tokens / sub-schemas
// ============================================================================

/**
 * Names of the palettes the renderer knows about. Stored by name
 * (not as a resolved object) so the wire format stays compact and
 * stable across token changes — the resolver in the webapp generator
 * maps `paletteName` to the current palette object at load time.
 */
export const CODING_BUDDY_PALETTE_NAMES = [
  'mint',
  'lavender',
  'rose',
  'honey',
  'sky',
  'coral',
  'sage',
  'blush',
] as const;

/** Tuft visual variant. Only meaningful when `hasTuft` is true. */
export const CODING_BUDDY_TUFT_STYLES = ['point', 'curl'] as const;

/**
 * Pattern allowed for a buddy's display name. Letters, digits,
 * spaces, and apostrophes; 1–24 characters. The strict charset
 * narrows the input surface for an SVG/HTML inlining context; the
 * apostrophe is allowed so possessive defaults like `${firstName}'s
 * Buddy` validate cleanly. Mirrored in the Firestore rule so a
 * hand-crafted SDK call can't bypass the client.
 */
export const BUDDY_NAME_PATTERN = /^[A-Za-z0-9 ']{1,24}$/;

/** Validated buddy display name. */
export const BuddyNameSchema = z
  .string()
  .regex(
    BUDDY_NAME_PATTERN,
    'Use letters, digits, spaces, and apostrophes (1–24 characters).',
  );

export type BuddyName = z.infer<typeof BuddyNameSchema>;

/**
 * State of a single problem on the buddy's roadmap.
 *
 * - `Unattempted` — the buddy hasn't started this problem.
 * - `InitialCode` — the buddy made an initial attempt; `currentCode`
 *   holds the buggy draft for the student to react to.
 * - `LiveSession` — a live teaching session is in flight (or just
 *   returned and is still being processed).
 * - `Finished` — the problem is solved.
 */
export const PROBLEM_STATUSES = [
  'Unattempted',
  'InitialCode',
  'LiveSession',
  'Finished',
] as const;

/**
 * Persisted entry for a single problem. `currentCode` is empty
 * until the buddy generates an attempt; the dashboard's attempt
 * modal reads it directly (no client-side stub catalogue).
 */
export const CodingBuddyProblemEntrySchema = z.object({
  status: z.enum(PROBLEM_STATUSES),
  currentCode: z.string(),
});

export type CodingBuddyProblemEntry = z.infer<
  typeof CodingBuddyProblemEntrySchema
>;

/**
 * Optional streak metadata. Server-written by the session-completion
 * trigger; clients should treat this as read-only. Absent until the
 * student has completed at least one session.
 */
export const CodingBuddyStreakSchema = z.object({
  /** Consecutive days the student has interacted with the buddy. */
  current: z.number().int().nonnegative(),
  /** When the last session that advanced the streak completed. */
  lastSessionAt: FirestoreTimestampSchema,
  /** Highest streak the student has ever held. Optional for backfills. */
  longest: z.number().int().nonnegative().optional(),
});

export type CodingBuddyStreak = z.infer<typeof CodingBuddyStreakSchema>;

// ============================================================================
// Top-level state schema
// ============================================================================

/**
 * Single per-user, per-course doc holding everything the Coding
 * Buddies feature persists. `.loose()` so adding new top-level
 * fields (earned accessories, dialogue prefs, etc.) doesn't break
 * older clients reading the doc.
 *
 * Stored at: `users/{userId}/{courseId}/codingBuddyState`
 */
export const CodingBuddyStateDocumentSchema = z
  .object({
    // ----- Identity ---------------------------------------------------------
    /** Display name the student chose; charset-locked by `BuddyNameSchema`. */
    name: BuddyNameSchema,
    /** Palette identifier; resolves to a `{body, dark}` pair in the renderer. */
    paletteName: z.enum(CODING_BUDDY_PALETTE_NAMES),
    /** Body width in 80×80 viewBox units. */
    bodyWidth: z.number(),
    /** Body height (unsquashed) in viewBox units. */
    bodyHeight: z.number(),
    /** Horizontal distance between the two eyes. */
    eyeSpacing: z.number(),
    /** Whether the buddy has pink cheeks. */
    hasCheeks: z.boolean(),
    /** Whether the buddy has a hair tuft. */
    hasTuft: z.boolean(),
    /** Tuft shape — ignored when `hasTuft` is false. */
    tuftStyle: z.enum(CODING_BUDDY_TUFT_STYLES),

    // ----- Progression ------------------------------------------------------
    /** Has the student played through the RPG intro? */
    hasSeenIntro: z.boolean(),
    /**
     * Per-problem entries keyed by `ProblemId`. The key set comes
     * from `BUDDY_PROBLEM_GRAPH` in the webapp; we don't enum the
     * keys here so adding a problem doesn't need a core release.
     */
    problems: z.record(z.string(), CodingBuddyProblemEntrySchema),
    /**
     * Buddy's "knowledge" — seed thoughts produced by the backend's
     * `coding_buddy/seed_thoughts` call at creation. Reused as the
     * `initial_thoughts` baseline for every buddy session
     * (`generate_code` and `start_session`) so the buddy's voice
     * stays consistent across the roadmap. Defaults to `[]` for
     * older docs written before the field existed.
     */
    knowledge: z.array(z.string()).default([]),

    // ----- Engagement -------------------------------------------------------
    /**
     * Streak metadata. Server-written; absent until the student
     * completes at least one session.
     */
    streak: CodingBuddyStreakSchema.optional(),

    // ----- Daily energy throttle --------------------------------------------
    /**
     * Remaining daily buddy energy. Client-managed (this is a soft
     * pedagogical throttle, not abuse prevention). Bounded to
     * `[0, 10]`; the client clamps on every spend and the optional
     * Firestore rule clamps on write. Absent on older docs and on
     * brand-new buddies; the client treats the absence as "refill on
     * first action."
     */
    energy: z.number().int().min(0).max(10).optional(),
    /**
     * Local-calendar date (`YYYY-MM-DD`) of the most recent refill.
     * The client compares its own local date against this every time
     * it hydrates or spends; a mismatch triggers a refill back to the
     * daily cap. Absent on older docs and on brand-new buddies, which
     * the client treats as "stale" so the next interaction refills.
     */
    energyRefilledOn: z.string().optional(),

    // ----- Daily streak -----------------------------------------------------
    /**
     * Consecutive days the student has interacted with the buddy.
     * Client-managed alongside `energy` — incremented on the first
     * activity of each new day (where "day" is the student's local
     * calendar day). Absent on older docs; the client treats the
     * absence as "no streak yet" and starts at 1 on first activity.
     */
    dailyStreakCount: z.number().int().nonnegative().optional(),
    /**
     * Local-calendar date (`YYYY-MM-DD`) the streak was last bumped.
     * Same-day activity holds the count; previous-day activity
     * increments; older or missing resets to 1.
     */
    dailyStreakDate: z.string().optional(),
  })
  .loose();

export type CodingBuddyStateDocument = z.infer<
  typeof CodingBuddyStateDocumentSchema
>;

// ============================================================================
// Paths
// ============================================================================

/**
 * Constructs the path to a buddy's state document for a specific
 * course.
 *
 * Document path: `users/{userId}/{courseId}/codingBuddyState`
 *
 * @param userId - The owning user.
 * @param courseId - The course whose buddy state is being addressed.
 * @returns The fully-qualified Firestore document path.
 */
export function getCodingBuddyStateDocumentPath({
  userId,
  courseId,
}: {
  userId: string;
  courseId: CourseId;
}): string {
  return `users/${userId}/${courseId}/codingBuddyState`;
}
